import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface EnvironmentIndicatorProps {
  className?: string;
}

interface EnvironmentConfig {
  name: string;
  label: string;
  bgColor: string;
  textColor: string;
  show: boolean;
}

const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = import.meta.env['VITE_APP_ENVIRONMENT'];
  const hostname = window.location.hostname;

  // Primary: Check environment variable if set
  if (env) {
    switch (env) {
      case 'development':
        return {
          name: 'Development',
          label: 'LOCAL',
          bgColor: 'bg-green-500',
          textColor: 'text-white',
          show: true
        };
      case 'staging':
        return {
          name: 'Staging',
          label: 'STAGING',
          bgColor: 'bg-orange-500',
          textColor: 'text-white',
          show: true
        };
      case 'production':
        return {
          name: 'Production',
          label: 'PRODUCTION',
          bgColor: '',
          textColor: '',
          show: false // Hide in production
        };
    }
  }

  // Fallback: Use hostname-based detection
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost')) {
    return {
      name: 'Development',
      label: 'LOCAL',
      bgColor: 'bg-green-500',
      textColor: 'text-white',
      show: true
    };
  }

  // Staging environment
  if (hostname === 'flexireo-staging.vercel.app' || hostname.includes('staging')) {
    return {
      name: 'Staging',
      label: 'STAGING',
      bgColor: 'bg-orange-500',
      textColor: 'text-white',
      show: true
    };
  }

  // Production environment (app.flexireo.com or any other domain)
  return {
    name: 'Production',
    label: 'PRODUCTION',
    bgColor: '',
    textColor: '',
    show: false // Hide in production
  };
};

export function EnvironmentIndicator({ className = '' }: EnvironmentIndicatorProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [config, setConfig] = useState<EnvironmentConfig | null>(null);

  useEffect(() => {
    const envConfig = getEnvironmentConfig();
    setConfig(envConfig);
    
    // Check localStorage for user preference
    const userPreference = localStorage.getItem('env-indicator-visible');
    if (userPreference !== null) {
      setIsVisible(JSON.parse(userPreference));
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('env-indicator-visible', 'false');
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new CustomEvent('env-indicator-toggle'));
  };

  const handleRestore = () => {
    setIsVisible(true);
    localStorage.setItem('env-indicator-visible', 'true');
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new CustomEvent('env-indicator-toggle'));
  };

  // Don't render if config is not loaded yet or environment should not show indicator
  if (!config || !config.show) {
    return null;
  }

  // If user has closed the banner, show a small restore button
  if (!isVisible) {
    return (
      <button
        onClick={handleRestore}
        className="fixed top-0 right-4 z-50 px-2 py-1 text-xs bg-gray-600 text-white rounded-b-md hover:bg-gray-700 transition-colors"
        title={`Show ${config.name} environment indicator`}
      >
        {config.label.charAt(0)}
      </button>
    );
  }

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 ${config.bgColor} ${config.textColor} ${className}`}
      style={{ height: '28px' }}
    >
      <div className="flex items-center justify-between h-full px-4">
        {/* Left section */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-current rounded-full opacity-75"></div>
            <span className="text-sm font-medium">
              {config.name} Environment
            </span>
          </div>
          <span className="text-xs opacity-75">
            {window.location.hostname}
          </span>
        </div>
        
        {/* Center section - Large environment label */}
        <div className="absolute left-1/2 transform -translate-x-1/2">
          <span className="text-lg font-bold tracking-wider">
            {config.label}
          </span>
        </div>
        
        {/* Right section */}
        <button
          onClick={handleClose}
          className="p-1 hover:bg-black/10 rounded transition-colors"
          title="Hide environment indicator"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}